import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { SubscribeCampaignConfig } from '../../config/types';
import { buildSubscribeSchema, SubscribeFormValues } from './SubscribeFormSchema';
import { useSubscribeSubmit } from './useSubscribeSubmit';
import { DEFAULT_SEND_BUTTON_TEXT } from '../../config/defaults';
import FirstNameField from './fields/FirstNameField';
import LastNameField from './fields/LastNameField';
import EmailField from './fields/EmailField';
import PhoneField from './fields/PhoneField';
import AgreeTestDriveField from './fields/AgreeTestDriveField';
import ConsentFields from './fields/ConsentFields';
import ExtraCheckboxes from './fields/ExtraCheckboxes';
import RichTopCopy from '../shared/RichTopCopy';
import GdprText from '../shared/GdprText';
import RecaptchaWidget from '../shared/RecaptchaWidget';
import Modal from '../shared/Modal';

interface Props {
  config: SubscribeCampaignConfig;
}

/**
 * The shared form engine: composes independent field components based on campaign config.
 * A brand-new, one-off layout does NOT have to use this — it can import the same field
 * components + useSubscribeSubmit()/buildSubscribeSchema() directly and assemble its own JSX.
 */
export default function SubscribeForm({ config }: Props) {
  const schema = buildSubscribeSchema(config);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SubscribeFormValues>({ resolver: zodResolver(schema) });
  const { onSubmit, isSubmitting, recaptchaRef, errorOpen, setErrorOpen } = useSubscribeSubmit(config);

  return (
    <>
      <form
        id="js-newsletter-form"
        className="form-container js-subscribe-form"
        data-form={config.syneriseEnabled ? config.formName || `${config.slug},newsletter` : ''}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="form-triangle-decor" />
        <div className="form-row">
          <div className="form-copy">
            <RichTopCopy html={config.topCopy} />
          </div>
        </div>

        <FirstNameField register={register} errors={errors} required={config.firstnameRequired} hidden={config.hideFirstnameField} />
        <LastNameField register={register} errors={errors} hidden={config.hideLastnameField} />

        {!config.disableEmailField && <EmailField register={register} errors={errors} />}

        {config.showPhone && <PhoneField register={register} errors={errors} required={config.phoneRequired} />}

        <div style={{ paddingBottom: 10 }}>&nbsp;</div>

        {config.showPhone && !config.hideAgreeTestDriveMessage && (
          <AgreeTestDriveField
            register={register}
            errors={errors}
            campaignName={config.name}
            messageOverride={config.agreeTestDriveMessage}
          />
        )}

        {!config.disableAgrees && <ConsentFields register={register} errors={errors} rulesPdf={config.rulesPdf} />}

        <ExtraCheckboxes
          register={register}
          setValue={setValue}
          watch={watch}
          label1={config.extraCheckbox1}
          label2={config.extraCheckbox2}
          header={config.extraHeader}
        />

        <div>
          <br />
        </div>

        {/* GDPR text comes BEFORE the submit button — matches form.html.twig's ordering
            exactly (_form-info-container include sits above the form-row--buttons block). */}
        <GdprText
          hasPhone={config.showPhone}
          obowiazekInformacyjny1={config.obowiazekInformacyjny1}
          obowiazekInformacyjny2={config.obowiazekInformacyjny2}
        />

        <div className="form-row form-row--buttons" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="button button--primary" style={{ flex: '0 0 48%' }} disabled={isSubmitting}>
            {config.sendButtonText || DEFAULT_SEND_BUTTON_TEXT}
          </button>
        </div>

        <RecaptchaWidget ref={recaptchaRef} />
      </form>

      {/* Deliberately a plain, generic Modal — not styled to match the TY page's hero-image
          layout. A failed submit should let the user retry without losing what they typed, so
          this stays on top of the (still-filled) form instead of navigating away. */}
      <Modal open={errorOpen} setOpen={setErrorOpen}>
        Wystąpił błąd! Przepraszamy, prosimy spróbować później.
      </Modal>
    </>
  );
}
